// Stock quantities are whole numbers of the item's base unit (docs/plans/phase1-stock.md §2.0).
//
// The base unit is the smallest unit the facility ever issues: a tablet, a capsule, a mL, a
// vial. Packs are converted to base units by multiplying by a whole number, which is exact.
// Nothing here holds a fractional quantity, because a stock count that is 0.1 of a tablet
// out can never be reconciled, and a double cannot even hold 0.1 exactly.
//
// A JavaScript `number` is used rather than `bigint`: a whole number of tablets fits
// comfortably inside Number.MAX_SAFE_INTEGER, and every function asserts that it does.

import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';

/**
 * The largest quantity one movement or one document line can carry: the Postgres
 * `integer` limit the ledger column uses. Balances are summed as `bigint` in the database.
 */
export const MAX_LINE_QUANTITY = 2_147_483_647;

export type QuantityErrorCode =
  | 'quantity.empty'
  | 'quantity.not_a_whole_number'
  | 'quantity.negative'
  | 'quantity.zero'
  | 'quantity.too_large';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const QUANTITY_ERRORS: Record<QuantityErrorCode, string> = {
  'quantity.empty': 'No quantity was entered',
  'quantity.not_a_whole_number': 'A quantity is a whole number of units',
  'quantity.negative': 'A quantity cannot be negative here',
  'quantity.zero': 'A quantity must be more than zero',
  'quantity.too_large': 'That quantity is too large',
};

export interface QuantityRules {
  /** Allow 0 (a count line that found nothing). Default false. */
  readonly allowZero?: boolean;
}

/**
 * Throws unless the value is a safe whole number. For values the domain has already
 * validated; use `parseQuantity` for what a person typed.
 */
export function assertWholeQuantity(value: number, label = 'quantity'): void {
  if (!Number.isSafeInteger(value)) throw new RangeError(`${label} must be a whole number`);
}

/**
 * Reads what a storekeeper types: `12`, `1,200`, `1 200`. Grouping must be well formed
 * (`1,20` is refused rather than guessed at). Decimals are refused: stock is counted in
 * whole base units.
 */
export function parseQuantity(
  text: string,
  rules: QuantityRules = {},
): Result<number, QuantityErrorCode> {
  const trimmed = text.trim();
  if (trimmed === '') return err('quantity.empty');
  if (trimmed.startsWith('-')) return err('quantity.negative');

  let digits: string;
  if (/^\d+$/.test(trimmed)) {
    digits = trimmed;
  } else if (/^\d{1,3}([, ]\d{3})+$/.test(trimmed)) {
    digits = trimmed.replace(/[, ]/g, '');
  } else {
    return err('quantity.not_a_whole_number');
  }

  // Compare as text before converting, so a 30-digit number never becomes a float.
  const withoutLeadingZeros = digits.replace(/^0+(?=\d)/, '');
  const max = String(MAX_LINE_QUANTITY);
  if (
    withoutLeadingZeros.length > max.length ||
    (withoutLeadingZeros.length === max.length && withoutLeadingZeros > max)
  ) {
    return err('quantity.too_large');
  }

  const value = Number(withoutLeadingZeros);
  if (value === 0 && rules.allowZero !== true) return err('quantity.zero');
  return ok(value);
}

/**
 * Packs to base units: 5 boxes of 100 tablets is 500 tablets. Both numbers must be
 * positive whole numbers. Throws if the product exceeds what one line can carry.
 */
export function packsToBaseUnits(packs: number, baseUnitsPerPack: number): number {
  assertPositiveWhole(packs, 'packs');
  assertPositiveWhole(baseUnitsPerPack, 'baseUnitsPerPack');
  const total = packs * baseUnitsPerPack;
  if (total > MAX_LINE_QUANTITY) throw new RangeError('Quantity exceeds the largest line quantity');
  return total;
}

export interface PackSplit {
  readonly packs: number;
  readonly loose: number;
}

/**
 * Splits a quantity of base units into whole packs and loose units, for display:
 * 320 tablets in boxes of 100 is 3 boxes and 20 tablets. Negative quantities split by
 * magnitude and keep the sign on both parts.
 */
export function splitIntoPacks(baseUnits: number, baseUnitsPerPack: number): PackSplit {
  assertWholeQuantity(baseUnits, 'baseUnits');
  assertPositiveWhole(baseUnitsPerPack, 'baseUnitsPerPack');
  const sign = baseUnits < 0 ? -1 : 1;
  const magnitude = Math.abs(baseUnits);
  const packs = Math.floor(magnitude / baseUnitsPerPack);
  const loose = magnitude - packs * baseUnitsPerPack;
  return { packs: packs === 0 ? 0 : sign * packs, loose: loose === 0 ? 0 : sign * loose };
}

/** Adds whole quantities, refusing to leave the safe-integer range. */
export function sumQuantities(values: Iterable<number>): number {
  let total = 0;
  for (const value of values) {
    assertWholeQuantity(value);
    total += value;
    if (!Number.isSafeInteger(total)) throw new RangeError('Quantity sum overflow');
  }
  return total;
}

function assertPositiveWhole(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value <= 0)
    throw new RangeError(`${label} must be a positive whole number`);
}
